import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useSelector } from "react-redux";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TablePagination,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import HttpService from "../services/httpService";
import { Link } from 'react-router-dom';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import EmailIcon from '@material-ui/icons/Email';
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "24px",
    width: "100%",
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: "#1C375D",
    color: "white",
    fontWeight: "bold",
    textAlign: "center",
    border: "1px solid #fff",
  },
  tableCell: {
    textAlign: "center",
    border: "1px solid #e0e0e0",
  },
  filterSection: {
    padding: "16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    marginBottom: "16px",
  },
  filterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  statusApproved: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  statusPending: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  statusRejected: {
    backgroundColor: "#f44336",
    color: "white",
  },
  actionButton: {
    margin: theme.spacing(0.5),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
}));

const Approval = ({ setLoading, setSnackInfo }) => {
  const classes = useStyles();
  const { userDetails } = useSelector((state) => state.user);

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Set default start date as 2022-01-01
  const getDefaultStartDate = () => {
    return "2022-01-01";
  };

  // Filter states - Simplified
  const [filters, setFilters] = useState({
    status: "", 
    startDate: getDefaultStartDate(),
    endDate: getCurrentDate(),
  });

  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [dashboardData, setDashboardData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});

  // Status options 
  const statusOptions = [
    { value: "", label: "Pending & Rejected (Default)" },
    { value: "All", label: "All Status" },
    { value: "Approved", label: "Approved" },
    { value: "Pending for Approval", label: "Pending for Approval" },
    { value: "Rejected", label: "Rejected" },
  ];

  useEffect(() => {
    if (userDetails._id) {
      fetchDashboardData();
    }
  }, [userDetails]);

  // Fetch dashboard data with filters and pagination
  const fetchDashboardData = async (
    filterParams = filters,
    page = pagination.page,
    limit = pagination.limit,
    isApplyingFilters = false
  ) => {
    if (!userDetails._id) return;

    setIsLoadingData(true);
    setLoading(true);
    
    try {
      
      if (isApplyingFilters) {
        setDashboardData([]);
      }

      // Prepare filter parameters for API call
      const params = {
        status: filterParams.status,
        startDate: filterParams.startDate,
        endDate: filterParams.endDate,
        page: page + 1, // Backend expects 1-based page number
        limit: limit,
      };


      // Replace with your actual API call
      const response = await HttpService.designApprovalDashboard(params);

      if (response.data && response.data.success) {
      
        setDashboardData(response.data.data || []);
        
        // Store current filters for display
        setCurrentFilters({
          status: response.data.filters?.currentStatus || filterParams.status || "Pending & Rejected",
          startDate: filterParams.startDate,
          endDate: filterParams.endDate,
        });
        
       
        if (response.data.pagination) {
          setPagination((prev) => ({
            ...prev,
            page: page,
            total: response.data.pagination.totalItems,
            totalPages: response.data.pagination.totalPages,
          }));
        }
      } else {

        setDashboardData([]);
      }
    } catch (error) {
      // Clear data on error
      setDashboardData([]);
      setSnackInfo(
        error.response?.data?.message || "Failed to fetch approval data",
        "error"
      );
    } finally {
      setIsLoadingData(false);
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setDashboardData([]);
    fetchDashboardData(filters, 0, pagination.limit, true);
  };

  const clearFilters = () => {

    const defaultFilters = {
      status: "", 
      startDate: getDefaultStartDate(),
      endDate: getCurrentDate(),
    };
    setFilters(defaultFilters);
    setPagination((prev) => ({ ...prev, page: 0 }));
    setDashboardData([]);
    fetchDashboardData(defaultFilters, 0, pagination.limit, true);
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    fetchDashboardData(filters, newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      limit: newLimit,
      page: 0,
    }));
    setDashboardData([]);
    fetchDashboardData(filters, 0, newLimit, true);
  };

  const handleSendApprovalReminder = async (project) => {
    if (!project) return;

    setLoading(true);
    try {
      const payload = {
        leadId: project.id,
        emailId: project.sentToEmail,
        raisedBy: project.requestedBy,
        category: project.category,
      };

      const response = await HttpService.sendApprovalReminder(payload);

      if (response.data && response.data.success) {
        setSnackInfo("Approval reminder sent successfully!", "success");
      } else {
        setSnackInfo(
          response.data?.message || "Failed to send approval reminder",
          "error"
        );
      }
    } catch (error) {
      console.error("Error sending approval reminder:", error);
      setSnackInfo(
        error.response?.data?.message || "Error sending approval reminder",
        "error"
      );
    }
    setLoading(false);
  };



  const getStatusChip = (status) => {
    // Normalize status for display
    let displayStatus = status;
    if (status === "Sent for Approval") {
      displayStatus = "Pending";
    }
    
    switch (displayStatus) {
      case "Approved":
        return <Chip icon={<CheckCircleIcon />} label="Approved" className={classes.statusApproved} size="small" />;
      case "Pending":
        return <Chip label="Pending" className={classes.statusPending} size="small" />;
      case "Rejected":
        return <Chip icon={<CancelIcon />} label="Rejected" className={classes.statusRejected} size="small" />;
      default:
        return <Chip label={displayStatus} size="small" />;
    }
  };

  return (
    <div className={classes.root}>
      <div>
        {/* Title with spacing below */}
        <Typography
          variant="h5"
          style={{
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "32px",
            textDecoration:'underline'
          }}
        >
          Design Approval Dashboard
        </Typography>
      </div>

      <Paper elevation={2}>
        {/* Simple Filter Section */}
        <div className={classes.filterSection}>
          <Grid container spacing={2} alignItems="center">
           
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Select Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  label="Status"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Start Date Filter */}
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="From Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* End Date Filter */}
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="To Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sm={12} md={3}>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={applyFilters}
                  size="small"
                  disabled={isLoadingData}
                >
                  {isLoadingData ? <CircularProgress size={20} /> : "Submit"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  size="small"
                  disabled={isLoadingData}
                >
                  Clear Filters
                </Button>
              </div>
            </Grid>
          </Grid>
        </div>

      

  

        {/* Table */}
        {!isLoadingData && (
          <>
            {/* Pagination Controls */}
            {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
              <Typography variant="body2" color="textSecondary">
                Total Records: {pagination.total}
              </Typography>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={pagination.total}
                rowsPerPage={pagination.limit}
                page={pagination.page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count}`
                }
              />
            </div> */}

            <TableContainer>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>Sl. No</TableCell>
                    <TableCell className={classes.headerCell}>Customer Name</TableCell>
                    <TableCell className={classes.headerCell}>Lead No.</TableCell>
                    <TableCell className={classes.headerCell}>Project No.</TableCell>
                    <TableCell className={classes.headerCell}>Lead Won Date</TableCell>
                    <TableCell className={classes.headerCell}>Budget</TableCell>
                    <TableCell className={classes.headerCell}>Category</TableCell>
                    <TableCell className={classes.headerCell}>Requested By</TableCell>
                    <TableCell className={classes.headerCell}>Requested Date</TableCell>
                    <TableCell className={classes.headerCell}>Pending Days</TableCell>
                    <TableCell className={classes.headerCell}>Assigned To / Action</TableCell>
                    <TableCell className={classes.headerCell}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData?.length > 0 ? (
                    dashboardData?.map((project, index) => (
                      <TableRow key={`${project.id}-${project.category}-${index}`} hover>
                        <TableCell className={classes.tableCell}>
                          {project.slNo || index + 1}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {project.customerName}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Link
                            to={`/project-logs/${project.id}`}
                            target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1C375D', fontWeight: 700 }}
                          >
                            {project.leadNo}
                          </Link>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Link
                            to={`/project-logs/${project.id}`}
                            target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1C375D', fontWeight: 700 }}
                          >
                            {project.projectNo}
                          </Link>
                        </TableCell>

                        <TableCell className={classes.tableCell}>
                          {
                            project?.leadWonDate 
                              ? moment(project.leadWonDate).format('DD-MM-YYYY')
                              : 'NA'
                          }                    
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {project.budget}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {project.category}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {project.requestedBy}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {project.acadSkpFileSentForApprovalDate}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                        {project.acadSkpApprovalDuration}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {project.assignedTo}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {getStatusChip(project.status)}
                          {project.status === "Sent for Approval" && (
                            <Tooltip title="Send Approval Reminder Email">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSendApprovalReminder(project)}
                                style={{ marginLeft: "10px" }}
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center" style={{ padding: '40px' }}>
                        <Typography variant="body1" color="textSecondary">
                          {isLoadingData ? "Loading..." : "No data found. Try adjusting your filters."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </div>
  );
};

const mapDispatchToProps = {
  setLoading,
  setSnackInfo,
};

export default connect(null, mapDispatchToProps)(Approval);

